import { useEffect, useState } from "react";
import { ImagePlus, Save, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ImageDropZone from "./ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

interface GalleryImage {
  id: string;
  slot: number;
  image_url: string;
  seo_caption: string;
  alt_text: string;
  original_filename: string | null;
  file_size: number | null;
}

const buildAltText = (caption: string) =>
  caption.trim()
    ? `${caption.trim()} - Taxi Essaouira, Transport Marrakech Essaouira, Transfert Aéroport`
    : "Taxi Essaouira - Transport Marrakech Essaouira - Transfert Aéroport";

const AdminGallery = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [caption, setCaption] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = async () => {
    const { data } = await (supabase as any).from("gallery_images").select("*").order("slot", { ascending: true });
    setImages(data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const uploadImage = async (file: File) => {
    if (caption.trim().length < 10) {
      toast.error("Décrivez l'image (min. 10 caractères) — sert d'attribut alt SEO obligatoire.");
      throw new Error("Description SEO requise");
    }
    const webpFile = await compressToWebP(file, { maxSizeMB: 0.35 });
    const maxSlot = images.length > 0 ? Math.max(...images.map((image) => image.slot)) : 0;
    const path = `gallery/client-${Date.now()}.webp`;
    const { error: uploadError } = await withUploadTimeout(
      supabase.storage.from("site-images").upload(path, webpFile, {
        contentType: webpFile.type,
        upsert: false,
      })
    );

    if (uploadError) throw new Error(uploadError.message);

    const { data: urlData } = supabase.storage.from("site-images").getPublicUrl(path);
    const { error } = await (supabase as any).from("gallery_images").insert({
      slot: maxSlot + 1,
      image_url: `${urlData.publicUrl}?t=${Date.now()}`,
      seo_caption: caption.trim(),
      alt_text: buildAltText(caption.trim()),
      original_filename: file.name,
      file_size: webpFile.size,
      is_active: true,
    });

    if (error) throw new Error("Erreur galerie : " + error.message);
    toast.success(caption.trim() ? "Image compressée en WebP et ajoutée !" : "Image ajoutée avec SEO automatique !");
    setCaption("");
    load();
  };

  const updateCaption = async (image: GalleryImage, nextCaption: string) => {
    setSavingId(image.id);
    const { error } = await (supabase as any)
      .from("gallery_images")
      .update({ seo_caption: nextCaption, alt_text: buildAltText(nextCaption) })
      .eq("id", image.id);
    if (error) toast.error("Erreur: " + error.message);
    else {
      toast.success("Description SEO mise à jour !");
      load();
    }
    setSavingId(null);
  };

  const deleteImage = async (id: string) => {
    const { error } = await (supabase as any).from("gallery_images").delete().eq("id", id);
    if (error) toast.error("Erreur: " + error.message);
    else {
      toast.success("Image supprimée de la galerie !");
      load();
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <ImagePlus className="h-5 w-5 text-primary" />
            Manage Gallery
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <label className="mb-1 block font-body text-xs font-semibold text-foreground">
              Texte alternatif (Alt) <span className="text-destructive">*</span>
            </label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="ex : Client VIP devant un van privé à Essaouira"
              required
            />
            <p className="mt-1 font-body text-[11px] text-muted-foreground">
              Décrivez l'image pour un malvoyant en incluant un mot-clé (ex : transfert aéroport Marrakech).
            </p>
          </div>
          <ImageDropZone onUpload={uploadImage} accept="image/*" label="Upload JPG, PNG, WEBP — compression WebP + SEO automatique" height="h-24" />
        </CardContent>
      </Card>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {images.map((image) => (
          <Card key={image.id} className="border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="font-heading text-base">Photo {image.slot}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => deleteImage(image.id)} className="text-destructive hover:text-destructive/80">
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <img src={image.image_url} alt={image.alt_text} className="h-40 w-full rounded-md bg-secondary object-cover" loading="lazy" />
              <Input
                defaultValue={image.seo_caption}
                onBlur={(e) => {
                  if (e.target.value !== image.seo_caption) updateCaption(image, e.target.value);
                }}
              />
              <p className="font-body text-xs text-muted-foreground">
                WebP optimisé {image.file_size ? `• ${Math.round(image.file_size / 1024)} Ko` : ""}
              </p>
              <Button
                type="button"
                size="sm"
                className="w-full"
                disabled={savingId === image.id}
                onClick={() => toast.info("Modifiez le texte puis cliquez hors du champ pour enregistrer.")}
              >
                <Save className="h-4 w-4" />
                {savingId === image.id ? "Enregistrement..." : "SEO actif"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminGallery;